import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import ClockIcon from "../../../assets/icons/Clock";
import LockedIcon from "../../../assets/icons/Locked";
import PeopleIcon from "../../../assets/icons/People";
import Button from "../../../components/Common/Button";
import ImageWithFallback from "../../../components/Common/ImageWithFallback";
import Progress from "../../../components/Common/Progress";
import SpinnerIcon from "../../../assets/icons/Spinner";
import type { MilestonePrize } from "../../../configs/apis/models";
import { useUpdateIsClaimedMutation } from "../../../configs/apis/UpdateIsClaimed";
import {
  formatNumber,
  formatRemainingTime,
  isPendingOrProcessingPrize,
} from "../../../configs/utils/utility";

export type ChallengeVariant = "claimable" | "inProgress" | "locked" | "active";

type ChallengeCallToActionProps = {
  isClaimingThis?: boolean;
  variant: ChallengeVariant;
  progress?: number;
  endDate?: string;
  onClaim?: (reward?: MilestonePrize) => void;
  peopleFinished?: number;
  prize?: MilestonePrize | undefined;
};

export type ChallengeCardProps = {
  id?: number;
  image: string;
  isClaimingThis?: boolean;
  name: string;
  categoryName: string;
  variant?: ChallengeVariant;
  skipImmediateClaim?: boolean;
} & ChallengeCallToActionProps;

const ChallengeCard = ({
  variant = "active",
  image,
  isClaimingThis,
  categoryName,
  prize,
  name,
  id,
  onClaim,
  skipImmediateClaim = false,
  ...props
}: ChallengeCardProps) => {
  const [updateIsClaimed] = useUpdateIsClaimedMutation();

  const claimChallenge = () => {
    updateIsClaimed({
      challengeId: id ?? 0,
    });
  };

  const handleCallToAction = (reward?: MilestonePrize) => {
    if (!skipImmediateClaim) {
      claimChallenge();
    }
    onClaim?.(reward);
  };

  return (
    <Link
      className="flex cursor-pointer flex-col justify-start"
      to={`/gamification/details/${id}`}
    >
      <div className="relative mb-2 rounded-xl shadow-md">
        {variant === "locked" && (
          <div className="absolute flex size-full items-center justify-center rounded-xl bg-black/45">
            <LockedIcon />
          </div>
        )}
        {categoryName && (
          <div className="absolute flex size-full items-end justify-start p-3 text-xs">
            <span className="rounded-full bg-white/50 px-2 py-1 text-foreground">
              {categoryName}
            </span>
          </div>
        )}
        <ImageWithFallback
          alt={name}
          className="aspect-square size-full rounded-xl object-cover"
          src={image}
        />
      </div>
      <div className="flex h-full flex-col justify-between">
        <h3 className="my-3 line-clamp-2 text-sm font-semibold">{name}</h3>
        <ChallengeCallToAction
          {...props}
          isClaimingThis={isClaimingThis}
          onClaim={handleCallToAction}
          prize={prize}
          variant={variant}
        />
      </div>
    </Link>
  );
};

const ChallengeCallToAction = ({
  isClaimingThis,
  variant,
  progress,
  peopleFinished,
  endDate,
  prize,
  onClaim,
}: ChallengeCallToActionProps) => {
  const { t: translate } = useTranslation("translation");
  const formattedRemaining = formatRemainingTime(endDate, translate);

  const isPendingOrProcessing = isPendingOrProcessingPrize(prize ? [prize] : undefined);

  switch (variant) {
  case "claimable":
    if (isPendingOrProcessing) {
      return (
        <Button
          disabled
          style={{ backgroundColor: "#fde7b5", color: "#000", border: "2px solid #d4a843" }}
        >
          <div aria-live="polite" aria-atomic="true">
            <span className="inline-flex items-center gap-2">
              <SpinnerIcon className="size-4 animate-spin motion-reduce:animate-none" />
              {translate("pending")}
            </span>
          </div>
        </Button>
      );
    }

    if (isClaimingThis) {
      return (
        <Button disabled>
          <div aria-live="polite" aria-atomic="true">
            <span className="inline-flex items-center gap-2">
              <SpinnerIcon className="size-4 animate-spin motion-reduce:animate-none" />
              {translate("processing")}
            </span>
          </div>
        </Button>
      );
    }

    return (
      <Button
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onClaim?.(prize);
        }}
      >
        {translate("view_reward")}
      </Button>
    );

  case "inProgress":
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Progress percentage={progress} />
          <span className="text-foreground-lighter">
            {progress?.toFixed(0)}%
          </span>
        </div>
        <div className="flex gap-1 text-sm text-foreground-lighter/80">
          <ClockIcon />
          <p>{formattedRemaining}</p>
        </div>
      </div>
    );

  default:
    return (
      <div className="flex justify-between text-sm text-foreground-lighter/80">
        <div className="flex gap-1">
          <PeopleIcon />
          <p>{formatNumber(peopleFinished || 0)}</p>
        </div>
        <div className="flex gap-1">
          <ClockIcon />
          <p>{formattedRemaining}</p>
        </div>
      </div>
    );
  }
};

export default ChallengeCard;
